@file:NoLiveLiterals

package com.example.borshi.web.wallet

import androidx.compose.runtime.*
import com.example.borshi.web.auth.UserProfile
    import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Import design system colors from AccountScreen
private const val BG_LIGHT = "#F4F9F9"
private const val TEXT_DARK = "#0D2B2E"
private const val PRIMARY_COLOR = "#D11243"
private const val BORDER_COLOR = "#E0E0E0"
private const val TEXT_GRAY = "#757575"
private const val PRIMARY_LIGHT = "#FFD1DC"
private const val SUCCESS_COLOR = "#10B981"
private const val ERROR_COLOR = "#EF4444"
private const val WARNING_COLOR = "#F59E0B"

/**
 * Main wallet section component
 * Displays wallet balance and transaction history
 */
@Composable
fun WalletSection(user: UserProfile) {
    // SECURITY: Ensure we have a valid, authenticated User ID from Firebase Auth
    // The passed 'user' object might be from local state; verify with actual auth provider
    val currentAuthId = com.example.borshi.web.firebase.FirebaseService.getCurrentUserId()

    if (user.userId == null || currentAuthId == null) {
        console.log("WalletSection: No authenticated user accessible (user.userId=${user.userId}, auth.uid=$currentAuthId)")
        UnauthenticatedWalletState()
        return
    }

    // Confirmed authenticated for debugging
    console.log("WalletSection: Starting wallet for user $currentAuthId")

    // Create wallet repository and view model
    val repository = remember {
        val fs = com.example.borshi.web.firebase.FirebaseService.getFirestore()
        val fn = com.example.borshi.web.firebase.FirebaseService.getFunctions()
        FirebaseWalletRepository(fs, fn) // Use proper dependency creation
    }
    val viewModel = remember {
        WalletViewModel(
            repository = repository,
            userId = currentAuthId,
            customerName = user.name ?: "Customer",
            customerPhone = user.phoneNumber ?: ""
        )
    }

    val walletState by viewModel.walletUiState.collectAsState()
    val transactionState by viewModel.transactionUiState.collectAsState()
    val filter by viewModel.filter.collectAsState()
    val operationResult by viewModel.operationResult.collectAsState()
    val isOperating by viewModel.isOperating.collectAsState()

    val scope = rememberCoroutineScope()

    DisposableEffect(Unit) {
        onDispose {
            viewModel.dispose()
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
        }
    }) {
        // Operation result message
        operationResult?.let { result ->
            OperationResultBanner(result) {
                viewModel.clearOperationResult()
            }
        }

        // Wallet balance card
        WalletBalanceCard(
            walletState = walletState,
            isOperating = isOperating,
            onAddMoney = { amount ->
                scope.launch {
                    viewModel.requestTopUp(amount)
                }
            }
        )

        // Transaction history
        TransactionHistoryCard(
            transactionState = transactionState,
            filter = filter,
            onFilterChange = { viewModel.setFilter(it) },
            onLoadMore = { viewModel.loadTransactions(reset = false) }
        )
    }
}

/**
 * Display when user is not authenticated
 */
@Composable
private fun UnauthenticatedWalletState() {
    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(16.px)
            padding(48.px, 32.px)
            textAlign("center")
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text("🔒")
        }

        H3({
            style {
                fontSize(24.px)
                fontWeight("600")
                color(Color(TEXT_DARK))
                margin(0.px, 0.px, 12.px, 0.px)
            }
        }) {
            Text("Sign In Required")
        }

        P({
            style {
                fontSize(16.px)
                color(Color(TEXT_GRAY))
                margin(0.px)
            }
        }) {
            Text("Please sign in to view your wallet and transactions")
        }
    }
}

/**
 * Wallet balance display card
 */
@Composable
private fun WalletBalanceCard(
    walletState: WalletUiState,
    isOperating: Boolean,
    onAddMoney: (Money) -> Unit
) {
    var showTopUpDialog by remember { mutableStateOf(false) }

    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(16.px)
            padding(32.px)
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(24.px)
            }
        }) {
            H3({
                style {
                    fontSize(20.px)
                    fontWeight("600")
                    color(Color(TEXT_DARK))
                    margin(0.px)
                }
            }) {
                Text("💰 My Wallet")
            }

            if (walletState is WalletUiState.Ready) {
                ActionButton(
                    text = "Add Money",
                    icon = "➕",
                    onClick = { showTopUpDialog = true },
                    disabled = isOperating
                )
            }
        }

        // Content based on state
        when (walletState) {
            is WalletUiState.Loading -> LoadingState()
            is WalletUiState.NotCreated -> EmptyWalletState { showTopUpDialog = true }
            is WalletUiState.Ready -> WalletBalanceContent(walletState.wallet)
            is WalletUiState.Error -> ErrorState(walletState.message)
        }
    }

    // Top-up dialog
    if (showTopUpDialog) {
        TopUpDialog(
            onConfirm = { amount ->
                onAddMoney(amount)
                showTopUpDialog = false
            },
            onDismiss = { showTopUpDialog = false }
        )
    }
}

/**
 * Display wallet balance details
 */
@Composable
private fun WalletBalanceContent(wallet: Wallet) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(20.px)
        }
    }) {
        // Available balance
        Div({
            style {
                backgroundColor(Color(BG_LIGHT))
                borderRadius(12.px)
                padding(24.px)
            }
        }) {
            P({
                style {
                    fontSize(14.px)
                    color(Color(TEXT_GRAY))
                    margin(0.px, 0.px, 8.px, 0.px)
                    fontWeight("500")
                }
            }) {
                Text("Available Balance")
            }

            P({
                style {
                    fontSize(36.px)
                    fontWeight("700")
                    color(Color(TEXT_DARK))
                    margin(0.px)
                }
            }) {
                Text(wallet.availableBalance.format())
            }
        }

        // Reserved balance (if any)
        if (wallet.reservedBalance.minorUnits > 0) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    padding(16.px)
                    backgroundColor(Color("#FEF3C7"))
                    borderRadius(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        color(Color(TEXT_DARK))
                        fontWeight("500")
                    }
                }) {
                    Text("Reserved Balance")
                }

                Span({
                    style {
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color(TEXT_DARK))
                    }
                }) {
                    Text(wallet.reservedBalance.format())
                }
            }
        }

        // Total balance
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                paddingTop(16.px)
                property("border-top", "1px solid $BORDER_COLOR")
            }
        }) {
            Span({
                style {
                    fontSize(16.px)
                    fontWeight("600")
                    color(Color(TEXT_DARK))
                }
            }) {
                Text("Total Balance")
            }

            Span({
                style {
                    fontSize(16.px)
                    fontWeight("700")
                    color(Color(PRIMARY_COLOR))
                }
            }) {
                Text(wallet.totalBalance.format())
            }
        }
    }
}

/**
 * Empty wallet state
 */
@Composable
private fun EmptyWalletState(onAddMoney: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(32.px, 16.px)
        }
    }) {
        Div({
            style {
                fontSize(64.px)
                marginBottom(16.px)
            }
        }) {
            Text("💳")
        }

        P({
            style {
                fontSize(18.px)
                fontWeight("600")
                color(Color(TEXT_DARK))
                margin(0.px, 0.px, 8.px, 0.px)
            }
        }) {
            Text("Your wallet is empty")
        }

        P({
            style {
                fontSize(14.px)
                color(Color(TEXT_GRAY))
                margin(0.px, 0.px, 24.px, 0.px)
            }
        }) {
            Text("Add money to start using your wallet for purchases")
        }

        ActionButton(
            text = "Add Money Now",
            icon = "➕",
            onClick = onAddMoney,
            primary = true
        )
    }
}

/**
 * Transaction history card
 */
@Composable
private fun TransactionHistoryCard(
    transactionState: TransactionUiState,
    filter: TransactionFilter,
    onFilterChange: (TransactionFilter) -> Unit,
    onLoadMore: () -> Unit
) {
    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(16.px)
            padding(32.px)
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
        }
    }) {
        // Header with filters
        Div({
            style {
                marginBottom(24.px)
            }
        }) {
            H3({
                style {
                    fontSize(20.px)
                    fontWeight("600")
                    color(Color(TEXT_DARK))
                    margin(0.px, 0.px, 16.px, 0.px)
                }
            }) {
                Text("📜 Transaction History")
            }

            // Filter chips
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                TransactionFilter.values().forEach { filterOption ->
                    FilterChip(
                        label = filterOption.label,
                        selected = filter == filterOption,
                        onClick = { onFilterChange(filterOption) }
                    )
                }
            }
        }

        // Transaction list
        when (transactionState) {
            is TransactionUiState.Loading -> LoadingState()
            is TransactionUiState.Empty -> EmptyTransactionsState()
            is TransactionUiState.Ready -> {
                TransactionList(transactionState.transactions)

                if (transactionState.hasMore) {
                    Div({
                        style {
                            textAlign("center")
                            marginTop(16.px)
                        }
                    }) {
                        ActionButton(
                            text = "Load More",
                            icon = "⬇️",
                            onClick = onLoadMore
                        )
                    }
                }
            }
            is TransactionUiState.Error -> ErrorState(transactionState.message)
        }
    }
}

/**
 * Transaction list
 */
@Composable
private fun TransactionList(transactions: List<WalletTransaction>) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        transactions.forEach { transaction ->
            TransactionItem(transaction)
        }
    }
}

/**
 * Single transaction item
 */
@Composable
private fun TransactionItem(transaction: WalletTransaction) {
    val isCredit = transaction.isCredit()
    val amountColor = when {
        isCredit -> SUCCESS_COLOR
        else -> ERROR_COLOR
    }
    val amountPrefix = if (isCredit) "+" else "-"

    var hovered by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(16.px)
            backgroundColor(if (hovered) Color(BORDER_COLOR) else Color(BG_LIGHT))
            borderRadius(12.px)
            property("transition", "all 0.2s ease")
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
    }) {
        // Left: Icon and details
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                flex("1")
            }
        }) {
            // Icon
            Div({
                style {
                    width(48.px)
                    height(48.px)
                    borderRadius(50.percent)
                    backgroundColor(Color.white)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(24.px)
                }
            }) {
                Text(transaction.type.icon)
            }

            // Details
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(4.px)
                }
            }) {
                P({
                    style {
                        fontSize(16.px)
                        fontWeight("600")
                        color(Color(TEXT_DARK))
                        margin(0.px)
                    }
                }) {
                    Text(transaction.type.displayName)
                }

                P({
                    style {
                        fontSize(14.px)
                        color(Color(TEXT_GRAY))
                        margin(0.px)
                    }
                }) {
                    val descriptionText = transaction.description?.takeIf { it.isNotBlank() }
                        ?: formatTimestamp(transaction.createdAt)
                    Text(descriptionText)
                }

                // Status badge
                TransactionStatusBadge(transaction.status)
            }
        }

        // Right: Amount
        Div({
            style {
                textAlign("right")
            }
        }) {
            P({
                style {
                    fontSize(18.px)
                    fontWeight("700")
                    color(Color(amountColor))
                    margin(0.px)
                }
            }) {
                Text("$amountPrefix${transaction.amount.format()}")
            }
        }
    }
}

/**
 * Transaction status badge
 */
@Composable
private fun TransactionStatusBadge(status: TransactionStatus) {
    val (bgColor, textColor, label) = when (status) {
        TransactionStatus.SUCCEEDED -> Triple("#D1FAE5", SUCCESS_COLOR, "✓ Completed")
        TransactionStatus.PENDING -> Triple("#FEF3C7", WARNING_COLOR, "⏳ Pending")
        TransactionStatus.FAILED -> Triple("#FEE2E2", ERROR_COLOR, "✗ Failed")
        TransactionStatus.CANCELLED -> Triple(BORDER_COLOR, TEXT_GRAY, "Cancelled")
    }

    Span({
        style {
            display(DisplayStyle.InlineBlock)
            padding(4.px, 12.px)
            backgroundColor(Color(bgColor))
            color(Color(textColor))
            fontSize(12.px)
            fontWeight("600")
            borderRadius(12.px)
        }
    }) {
        Text(label)
    }
}

/**
 * Empty transactions state
 */
@Composable
private fun EmptyTransactionsState() {
    Div({
        style {
            textAlign("center")
            padding(48.px, 16.px)
        }
    }) {
        Div({
            style {
                fontSize(64.px)
                marginBottom(16.px)
            }
        }) {
            Text("📭")
        }

        P({
            style {
                fontSize(16.px)
                fontWeight("600")
                color(Color(TEXT_DARK))
                margin(0.px, 0.px, 8.px, 0.px)
            }
        }) {
            Text("No transactions yet")
        }

        P({
            style {
                fontSize(14.px)
                color(Color(TEXT_GRAY))
                margin(0.px)
            }
        }) {
            Text("Your transaction history will appear here")
        }
    }
}

/**
 * Top-up dialog — Cashfree only.
 */
@Composable
private fun TopUpDialog(
    onConfirm: (Money) -> Unit,
    onDismiss: () -> Unit
) {
    var amount by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }

    // Modal overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { onDismiss() }
    }) {
        // Dialog content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(16.px)
                padding(32.px)
                maxWidth(400.px)
                width(90.percent)
                property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            H3({
                style {
                    fontSize(24.px)
                    fontWeight("600")
                    color(Color(TEXT_DARK))
                    margin(0.px, 0.px, 24.px, 0.px)
                }
            }) {
                Text("💳 Add Money")
            }

            // Amount input
            Div({
                style {
                    marginBottom(24.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Block)
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color(TEXT_DARK))
                        marginBottom(8.px)
                    }
                }) {
                    Text("Amount (₹)")
                }

                Input(InputType.Number) {
                    style {
                        width(100.percent)
                        padding(12.px)
                        fontSize(16.px)
                        border(2.px, LineStyle.Solid, Color(BORDER_COLOR))
                        borderRadius(8.px)
                        property("box-sizing", "border-box")
                    }
                    value(amount)
                    onInput { evt ->
                        amount = evt.value.toString()
                    }
                    attr("placeholder", "Enter amount")
                    attr("min", "10")
                    attr("step", "1")
                }

                error?.let { errorMsg ->
                    P({
                        style {
                            fontSize(12.px)
                            color(Color(ERROR_COLOR))
                            marginTop(8.px)
                            margin(8.px, 0.px, 0.px, 0.px)
                        }
                    }) {
                        Text(errorMsg)
                    }
                }
            }

            // Quick amount buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(24.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                listOf(100, 500, 1000, 2000, 5000).forEach { quickAmount ->
                    QuickAmountButton(quickAmount) { amount = quickAmount.toString() }
                }
            }

            // Pay with Cashfree
            Div({
                style {
                    marginBottom(16.px)
                    fontSize(14.px)
                    fontWeight("600")
                    color(Color(TEXT_DARK))
                }
            }) {
                Text("Pay with Cashfree")
            }
            Button({
                style {
                    width(100.percent)
                    padding(12.px, 16.px)
                    fontSize(14.px)
                    fontWeight("600")
                    border(2.px, LineStyle.Solid, Color("#D11243"))
                    backgroundColor(Color("#D11243"))
                    color(Color.white)
                    borderRadius(8.px)
                    cursor("pointer")
                    marginBottom(24.px)
                }
                onClick {
                    val amountValue = amount.toDoubleOrNull()
                    when {
                        amountValue == null -> error = "Please enter a valid amount"
                        amountValue < 10 -> error = "Minimum amount is ₹10"
                        amountValue > 100000 -> error = "Maximum amount is ₹1,00,000"
                        else -> onConfirm(Money.fromRupees(amountValue))
                    }
                }
            }) {
                Text("Cashfree — Card, UPI, NetBanking")
            }

            // Actions
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                }
            }) {
                Button({
                    style {
                        flex("1")
                        padding(12.px, 24.px)
                        fontSize(16.px)
                        fontWeight("600")
                        border(2.px, LineStyle.Solid, Color(BORDER_COLOR))
                        backgroundColor(Color.white)
                        color(Color(TEXT_DARK))
                        borderRadius(8.px)
                        cursor("pointer")
                    }
                    onClick { onDismiss() }
                }) {
                    Text("Cancel")
                }
            }
        }
    }
}

// Helper composables and extensions

@Composable
private fun ActionButton(
    text: String,
    icon: String,
    onClick: () -> Unit,
    primary: Boolean = false,
    disabled: Boolean = false
) {
    var hovered by remember { mutableStateOf(false) }

    Button({
        style {
            padding(12.px, 24.px)
            fontSize(15.px)
            fontWeight("600")
            borderRadius(8.px)
            cursor(if (disabled) "not-allowed" else "pointer")
            property("transition", "all 0.3s ease")
            opacity(if (disabled) 0.5 else 1)

            if (primary) {
                backgroundColor(if (hovered) Color("#B00D38") else Color(PRIMARY_COLOR))
                color(Color.white)
                border(0.px)
            } else {
                backgroundColor(if (hovered) Color(BG_LIGHT) else Color.white)
                color(Color(PRIMARY_COLOR))
                border(2.px, LineStyle.Solid, Color(PRIMARY_COLOR))
            }
        }
        onMouseEnter { if (!disabled) hovered = true }
        onMouseLeave { hovered = false }
        onClick { if (!disabled) onClick() }
        if (disabled) {
            attr("disabled", "true")
        }
    }) {
        Text("$icon $text")
    }
}

@Composable
private fun FilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }

    Button({
        style {
            padding(8.px, 16.px)
            fontSize(14.px)
            fontWeight("600")
            borderRadius(20.px)
            cursor("pointer")
            property("transition", "all 0.2s ease")
            border(0.px)

            if (selected) {
                backgroundColor(Color(PRIMARY_COLOR))
                color(Color.white)
            } else {
                backgroundColor(if (hovered) Color(BG_LIGHT) else Color.white)
                color(Color(TEXT_DARK))
                border(1.px, LineStyle.Solid, Color(BORDER_COLOR))
            }
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun QuickAmountButton(amount: Int, onClick: () -> Unit) {
    var hovered by remember { mutableStateOf(false) }

    Button({
        style {
            padding(8.px, 16.px)
            fontSize(14.px)
            fontWeight("600")
            borderRadius(8.px)
            cursor("pointer")
            property("transition", "all 0.2s ease")
            backgroundColor(if (hovered) Color(PRIMARY_LIGHT) else Color(BG_LIGHT))
            color(Color(TEXT_DARK))
            border(1.px, LineStyle.Solid, Color(BORDER_COLOR))
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
        onClick { onClick() }
    }) {
        Text("₹$amount")
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            textAlign("center")
            padding(32.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                property("animation", "pulse 2s ease-in-out infinite")
            }
        }) {
            Text("⏳")
        }
        P({
            style {
                fontSize(14.px)
                color(Color(TEXT_GRAY))
                marginTop(12.px)
            }
        }) {
            Text("Loading...")
        }
    }
}

@Composable
private fun ErrorState(message: String) {
    Div({
        style {
            textAlign("center")
            padding(32.px)
            backgroundColor(Color("#FEE2E2"))
            borderRadius(12.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(12.px)
            }
        }) {
            Text("⚠️")
        }
        P({
            style {
                fontSize(16.px)
                fontWeight("600")
                color(Color(ERROR_COLOR))
                margin(0.px)
            }
        }) {
            Text(message)
        }
    }
}

@Composable
private fun OperationResultBanner(result: OperationResult, onDismiss: () -> Unit) {
    val (bgColor, textColor, icon) = when (result) {
        is OperationResult.Success -> Triple("#D1FAE5", SUCCESS_COLOR, "✓")
        is OperationResult.Failure -> Triple("#FEE2E2", ERROR_COLOR, "✗")
    }

    val message = when (result) {
        is OperationResult.Success -> result.message
        is OperationResult.Failure -> result.message
    }

    Div({
        style {
            backgroundColor(Color(bgColor))
            color(Color(textColor))
            padding(16.px, 24.px)
            borderRadius(12.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.1)")
        }
    }) {
        Span({
            style {
                fontSize(16.px)
                fontWeight("600")
            }
        }) {
            Text("$icon $message")
        }

        Button({
            style {
                backgroundColor(Color.transparent)
                border(0.px)
                color(Color(textColor))
                fontSize(20.px)
                cursor("pointer")
                padding(0.px)
            }
            onClick { onDismiss() }
        }) {
            Text("×")
        }
    }
}

// Helper extensions
private val TransactionType.icon: String
    get() = when (this) {
        TransactionType.TOPUP -> "💰"
        TransactionType.PURCHASE -> "🛒"
        TransactionType.REFUND -> "↩️"
        TransactionType.ADJUSTMENT -> "⚙️"
        TransactionType.HOLD -> "🔒"
        TransactionType.RELEASE -> "🔓"
        TransactionType.CAPTURE -> "✅"
        TransactionType.WITHDRAWAL -> "💸"
    }

private val TransactionType.displayName: String
    get() = when (this) {
        TransactionType.TOPUP -> "Money Added"
        TransactionType.PURCHASE -> "Purchase"
        TransactionType.REFUND -> "Refund"
        TransactionType.ADJUSTMENT -> "Adjustment"
        TransactionType.HOLD -> "Funds Held"
        TransactionType.RELEASE -> "Funds Released"
        TransactionType.CAPTURE -> "Payment Captured"
        TransactionType.WITHDRAWAL -> "Withdrawal"
    }

private val TransactionFilter.label: String
    get() = when (this) {
        TransactionFilter.ALL -> "All"
        TransactionFilter.PURCHASES -> "Purchases"
        TransactionFilter.REFUNDS -> "Refunds"
        TransactionFilter.TOPUPS -> "Top-ups"
    }

private fun formatTimestamp(epochMillis: Long): String {
    val date = kotlin.js.Date(epochMillis.toDouble())
    val now = kotlin.js.Date()

    val diffMillis = now.getTime() - date.getTime()
    val diffMinutes = (diffMillis / (1000 * 60)).toInt()
    val diffHours = (diffMillis / (1000 * 60 * 60)).toInt()
    val diffDays = (diffMillis / (1000 * 60 * 60 * 24)).toInt()

    return when {
        diffMinutes < 1 -> "Just now"
        diffMinutes < 60 -> "$diffMinutes min ago"
        diffHours < 24 -> "$diffHours hour${if (diffHours > 1) "s" else ""} ago"
        diffDays < 7 -> "$diffDays day${if (diffDays > 1) "s" else ""} ago"
        else -> {
            val day = date.getDate()
            val month = date.getMonth() + 1
            val year = date.getFullYear()
            "$day/${month}/$year"
        }
    }
}

/**
 * Create wallet repository instance
 * TODO: Inject via dependency injection
 */
private fun createWalletRepository(): WalletRepository {
    // Get Firebase instances from FirebaseService
    val firestore = com.example.borshi.web.firebase.FirebaseService.getFirestore()
    val functions = com.example.borshi.web.firebase.FirebaseService.getFunctions()

    return FirebaseWalletRepository(firestore, functions)
}

/**
 * Collect StateFlow as State in Compose
 */
@Composable
private fun <T> kotlinx.coroutines.flow.StateFlow<T>.collectAsState(): State<T> {
    val state = remember { mutableStateOf(value) }

    DisposableEffect(this) {
        val scope = kotlinx.coroutines.MainScope()
        val handler = kotlinx.coroutines.CoroutineExceptionHandler { _, throwable ->
            // Only log if it's not a cancellation
            if (throwable !is kotlinx.coroutines.CancellationException) {
                console.error("Error in collectAsState:", throwable)
            }
        }
        val job = scope.launch(handler) {
            try {
                collect { state.value = it }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Expected during disposal - ignore
            } catch (e: Throwable) {
                console.error("Error collecting flow:", e)
            }
        }
        onDispose {
            job.cancel()
            scope.cancel()
        }
    }

    return state
}
